import { BlocksViewCommands, Controller, InteractionMode } from '../types';
import {
  DEFAULT_VIEW_CONTEXT,
  DisabledZoomControls,
} from '@ardoq/view-settings';
import { createViewpointInteractions } from './viewpointInteractions';
import { createPresentationInteractions } from './presentationInteractions';
import type { I18nLocale } from '@ardoq/i18n';
import { redrawUntilCanvasImagesLoad } from 'tabview/canvasRendering/canvasResolvedImages';

export type RelationshipsViewLegendRef = {
  setIsVisible: (isVisible: boolean) => void;
  getIsVisible: () => boolean;
};
type ViewContextDependencies = {
  controllerRef: React.RefObject<Controller | null>;
  containerRef: React.RefObject<HTMLDivElement | null>;
  isLegendActive?: boolean;
  i18nLocale: I18nLocale;
  viewInstanceId: string;
  commands: BlocksViewCommands;
};
const createViewContext = ({
  controllerRef,
  containerRef,
  isLegendActive = false,
  i18nLocale,
  viewInstanceId,
  commands,
}: ViewContextDependencies) => ({
  ...DEFAULT_VIEW_CONTEXT,
  getContainer: () => containerRef.current,
  createExportCanvas: async () => {
    await redrawUntilCanvasImagesLoad(() =>
      controllerRef.current?.redrawImmediate()
    );
    return controllerRef.current?.createExportCanvas() ?? null;
  },
  zoomControls: controllerRef.current?.viewActionsConfigs(i18nLocale) ?? [],
  zoomControlsDisabledState: DisabledZoomControls.NONE,
  toggleLegend: () => {
    commands.onLegendToggle(!isLegendActive);
  },
  isLegendActive: isLegendActive,
  viewInstanceId,
});

export const updateController = ({
  controllerRef,
  containerRef,
  isLegendActive,
  i18nLocale,
  viewInstanceId,
  commands,
}: ViewContextDependencies) => {
  /* this is called by the controller when it wants to persuade the app to refresh the view context */
  /* But it seems to do more than that? Not sure what exactly though */

  const controller = controllerRef.current;

  if (!controller) {
    return;
  }

  const interactionMode = controller.getInteractionMode();

  const createInteractions =
    interactionMode === InteractionMode.Viewpoint
      ? createViewpointInteractions
      : createPresentationInteractions;

  controller.attach(createInteractions(controller));
  controller.renderCanvas();

  createAndUpdateViewContext({
    controllerRef,
    containerRef,
    isLegendActive,
    i18nLocale,
    viewInstanceId,
    commands,
  });
};

export const createAndUpdateViewContext = (
  viewContextDependencies: ViewContextDependencies
) =>
  viewContextDependencies.commands.updateViewContext(
    createViewContext(viewContextDependencies)
  );
